import type { FooterConfig } from '@/lib/sections/types';
import { collectionUrl } from '@/lib/collectionUrl';
import { canonicalPath } from '@workspace/url-source';
import { BUKIETOWA_ORGANIZATION } from '@/lib/globalFloristSchema';

/**
 * Jedyne źródło prawdy dla treści stopki — używane zarówno gdy Footer jest
 * renderowany bezpośrednio na podstronach (domyślny prop w Footer.tsx), jak
 * i przez sekcję 'footer' na stronie głównej (data/homeSections.config.ts).
 * Wcześniej te dwa miejsca trzymały niezależne kopie tej samej treści, które
 * się rozjechały (link "Flower Box" wskazywał na dwa różne, choć równoważne,
 * adresy) — ten plik eliminuje ten duplikat.
 */
export const FOOTER_CONFIG: FooterConfig = {
  brandName: 'Bukietowa.pl',
  tagline: 'Kwiaty z emocjami',
  description: 'Bukiety, flowerboxy, kwiaty doniczkowe i wiele więcej – z miłości do pięknych chwil.',
  copyright: '© 2025. Bukietowa.pl. Wszelkie prawa zastrzeżone.',
  linkColumns: [
    {
      title: 'Sklep',
      links: [
        { label: 'Bestsellery',       href: collectionUrl('bestsellery') },
        { label: 'Bukiety',           href: collectionUrl('bukiety') },
        { label: 'Flower Boxy',       href: collectionUrl('flower-boxy') },
        { label: 'Kwiaty doniczkowe', href: collectionUrl('kwiaty-doniczkowe') },
        { label: 'Kwiaty w koszu',    href: collectionUrl('kosze-kwiatowe') },
        { label: 'Balony z helem',    href: collectionUrl('balony-z-helem') },
        { label: 'Wieńce pogrzebowe', href: collectionUrl('wience-pogrzebowe') },
        { label: 'Kreator Bukietów',  href: canonicalPath('/kreatory-bukietow') },
      ],
    },
    {
      title: 'Na jaką okazję',
      links: [
        { label: 'Urodziny',      href: collectionUrl('urodziny') },
        { label: 'Imieniny',      href: collectionUrl('imieniny') },
        { label: 'Zaręczyny',     href: collectionUrl('zareczynny') },
        { label: 'Przeprosiny',   href: collectionUrl('przeprosiny') },
        { label: 'Gratulacje',    href: collectionUrl('gratulacje') },
        { label: 'Podziękowania', href: collectionUrl('podziekowania') },
      ],
    },
    {
      title: 'Oferta',
      links: [
        { label: 'Ślub', href: canonicalPath('/slub') },
        { label: 'Dla firm', href: canonicalPath('/dla-firm') },
        { label: 'Zamówienie specjalne', href: canonicalPath('/zamowienie-specjalne') },
        { label: 'Warsztaty florystyczne', href: canonicalPath('/warsztaty-florystyczne') },
      ],
    },
    {
      title: 'Informacje',
      links: [
        { label: 'Dostawa i płatność', href: canonicalPath('/dostawa') },
        { label: 'Płatności', href: canonicalPath('/platnosci') },
        { label: 'Regulamin', href: canonicalPath('/regulamin') },
        { label: 'Polityka prywatności', href: canonicalPath('/prywatnosc') },
        { label: 'Polityka zwrotów', href: canonicalPath('/polityka-zwrotow') },
        { label: 'FAQ', href: canonicalPath('/faq') },
        { label: 'Blog', href: canonicalPath('/blog') },
        { label: 'Kontakt', href: canonicalPath('/kontakt') },
      ],
    },
  ],
  socialLinks: [
    { iconKey: 'facebook', href: BUKIETOWA_ORGANIZATION.sameAs[0], label: 'Facebook' },
    { iconKey: 'instagram', href: BUKIETOWA_ORGANIZATION.sameAs[1], label: 'Instagram' },
  ],
  deliveryArea: {
    ariaLabel: 'Obszar dostawy kwiatów - Lublin i okolice',
    mobileHeading: 'Obszar dostawy - Lublin i okolice',
    tabs: [
      {
        key: 'districts',
        iconKey: 'mapPin',
        mobileTitle: 'Dzielnice Lublina',
        desktopTitle: 'Wszystkie dzielnice Lublina',
        desktopDescriptionLines: [
          'Dostarczamy kwiaty do wszystkich dzielnic Lublina.',
          'Sprawdź, czy dowozimy również do Ciebie.',
        ],
        hrefPrefix: '/kwiaciarnia-lublin',
        navigationLabel: 'Dzielnice Lublina',
      },
      {
        key: 'nearby',
        iconKey: 'truck',
        mobileTitle: 'Okolice',
        desktopTitle: 'Najbliższe okolice',
        desktopDescriptionLines: [
          'Dostarczamy również kwiaty do miejscowości położonych w najbliższej okolicy Lublina.',
        ],
        hrefPrefix: '/kwiaciarnia-okolice',
        navigationLabel: 'Miejscowości w okolicy Lublina',
      },
    ],
  },
  benefits: [
    { iconKey: 'truck', title: 'Dostawa', description: 'tego samego dnia - sprawdź dostępność' },
    { iconKey: 'sparkles', title: 'Świeże kwiaty', description: 'najwyższej jakości' },
    { iconKey: 'shieldCheck', title: 'Pomoc', description: 'w wyborze kwiatów' },
  ],
  payments: {
    ariaLabel: 'Akceptowane metody płatności',
    methods: [
      { logoKey: 'visa', label: 'VISA' },
      { logoKey: 'mastercard', label: 'Mastercard' },
      { logoKey: 'blik', label: 'BLIK' },
      { logoKey: 'przelewy24', label: 'Przelewy24' },
    ],
  },
  bottomLinks: [
    { label: 'Dostawa kwiatów',    href: canonicalPath('/dostawa') },
    { label: 'Bukiety Lublin',     href: canonicalPath('/bukiety') },
    { label: 'Kwiaciarnia online', href: '/' },
    { label: 'Flower Box',         href: canonicalPath('/flower-boxy') },
    { label: 'Kwiaty z dostawą',   href: canonicalPath('/kwiaty') },
  ],
};
